import { useState } from 'react';

import { Alert, Button, TextField, Typography } from '@/ui';

import { useRequestAssistantAccessMutation } from './api';

import type { AssistantExternalRef } from './api';
import type { FC, FormEvent } from 'react';

interface AssistantRequestLinkFormProps {
  /** The invite the assistant came from; names whose calendar they manage. */
  externalRef?: AssistantExternalRef;
}

/**
 * Asks Moments to email a sign-in link. The answer is the same whether or not
 * the address belongs to an assistant, so the page never says which it is.
 */
export const AssistantRequestLinkForm: FC<AssistantRequestLinkFormProps> = ({
  externalRef,
}) => {
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [requestAccess, { isError, isLoading, reset }] =
    useRequestAssistantAccessMutation();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    try {
      await requestAccess({ email: address, externalRef }).unwrap();
      setSentTo(address);
    } catch {
      // Shown below; only a transport or server failure lands here.
    }
  };

  if (sentTo) {
    return (
      <div className="flex flex-col gap-3" role="status">
        <Typography variant="h5">Check your inbox</Typography>
        <Typography variant="body1">
          If {sentTo} is set up as a scheduling assistant, we&apos;ve sent a
          sign-in link. It works once and expires soon.
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Nothing arrived? Ask the person whose calendar you manage to check
          that you&apos;re added as their scheduling assistant in Mento.
        </Typography>
        <div>
          <Button
            variant="text"
            onClick={() => {
              setSentTo(null);
              reset();
            }}
          >
            Use a different email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
      <TextField
        autoComplete="email"
        inputProps={{ 'aria-label': 'Your email' }}
        label="Your email"
        type="email"
        value={email}
        fullWidth
        onChange={(event) => setEmail(event.target.value)}
      />
      {isError ? (
        <Alert severity="error">
          We couldn&apos;t send that request. Please try again.
        </Alert>
      ) : null}
      <div>
        <Button
          disabled={isLoading || !email.trim()}
          type="submit"
          variant="contained"
        >
          Email me a sign-in link
        </Button>
      </div>
    </form>
  );
};
